/**
 * Ambient telemetry strip that sits above the global header on every
 * authenticated page. Chips: regime · spot+Δ · flip · king · status+age · time.
 * Collapses to a single tight chip on phones. Hidden on public/login routes.
 *
 * Ticker resolution: when an explicit `ticker` prop is passed (AppShell
 * currently pins this to SPX), that wins and the strip is a fixed market
 * reference. With no prop, it auto-follows the URL hash (e.g.
 * `/app/options#QQQ`), falling back to SPX for an empty/invalid hash.
 */

import React, { useEffect, useState } from 'react';
import { useAmbientTelemetry, type ConnectionStatus } from '../../hooks/useAmbientTelemetry';
import { useTween } from '../../hooks/useTween';
import { formatStrike, formatClock } from '../../lib/format';

interface AmbientTickerProps {
  /** Explicit override — omit to auto-resolve from URL hash. */
  ticker?: string;
}

/** Parse an uppercase ticker from a URL hash (e.g. "#QQQ" → "QQQ"). */
function parseHashTicker(hash: string): string | null {
  // Strip leading '#', trim whitespace, uppercase.
  const candidate = hash.replace(/^#/, '').trim().toUpperCase();
  // Mirrors backend _is_admissible_ticker + useHashTicker: letter-led,
  // 1-10 chars, allows . and - for symbols like BRK.B. Earlier regex
  // capped at 6 chars and rejected dot/hyphen — that worked for the
  // always-on pill list but broke open-universe symbols and BRK.B.
  return /^[A-Z][A-Z0-9.\-]{0,9}$/.test(candidate) ? candidate : null;
}

const FALLBACK_TICKER = 'SPX';

function fmtMoney(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtSignedPct(n: number | null): string {
  if (n == null) return '—';
  const s = n >= 0 ? '+' : '';
  return `${s}${(n * 100).toFixed(2)}%`;
}

function fmtSigned(n: number | null): string {
  if (n == null) return '—';
  const s = n >= 0 ? '+' : '';
  return `${s}${n.toFixed(2)}`;
}

function statusLabel(status: ConnectionStatus, age: number | null): string {
  switch (status) {
    case 'streaming': return age != null ? `STREAMING · ${Math.floor(age)}s ago` : 'STREAMING';
    case 'stale':     return age != null ? `STALE · ${Math.floor(age)}s ago` : 'STALE';
    case 'polling':   return 'POLLING FALLBACK';
    case 'offline':   return 'OFFLINE — RETRYING…';
  }
}

function statusDotClass(status: ConnectionStatus): string {
  switch (status) {
    case 'streaming': return 'live';
    case 'stale':     return 'warn';
    case 'polling':   return 'warn';
    case 'offline':   return 'dead';
  }
}

const AgeChip: React.FC<{ status: ConnectionStatus; lastUpdate: Date | null }> = React.memo(function AgeChip({ status, lastUpdate }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force(x => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const age = lastUpdate ? (Date.now() - lastUpdate.getTime()) / 1000 : null;
  return (
    <span className={`gv-tick optional`}>
      <span className={`dot ${statusDotClass(status)}`} aria-hidden="true" />
      <span className="label">{statusLabel(status, age)}</span>
    </span>
  );
});

// Wall-clock time chip — relocated here from the old Options status bar.
// Ticks once a second; isolated in its own memo so the 1 s re-render
// doesn't churn the telemetry chips. Local 24-hour time (HH:MM:SS).
const ClockChip: React.FC = React.memo(function ClockChip() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="gv-tick optional">
      <span className="label">TIME</span>
      <span className="val">{formatClock(now)}</span>
    </span>
  );
});

const AmbientTicker: React.FC<AmbientTickerProps> = ({ ticker: tickerProp }) => {
  // Resolve ticker from URL hash when no explicit prop is provided.
  // TickerPills uses history.replaceState (not a router navigate), so
  // useLocation() won't re-render on hash change. We listen for the
  // native `hashchange` event instead and read window.location.hash
  // directly — this is the same pattern useHashTicker uses.
  const [hashTicker, setHashTicker] = useState<string>(() => {
    if (tickerProp) return tickerProp;
    return parseHashTicker(window.location.hash) ?? FALLBACK_TICKER;
  });

  useEffect(() => {
    if (tickerProp) return; // explicit prop overrides — no listener needed
    const onHashChange = () => {
      setHashTicker(parseHashTicker(window.location.hash) ?? FALLBACK_TICKER);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [tickerProp]);

  const ticker = tickerProp ?? hashTicker;

  const t = useAmbientTelemetry({ ticker });
  const tweenedSpot = useTween(t.spot, 600);
  const tweenedPct = useTween(t.spotChangePct, 600);

  // Direction flash: when Δ% sign changes, briefly tint the row.
  const prevSign = React.useRef<number | null>(null);
  const [flash, setFlash] = useState<'pos' | 'neg' | null>(null);
  useEffect(() => {
    if (t.spotChangePct == null) return;
    const sign = Math.sign(t.spotChangePct);
    if (prevSign.current != null && sign !== prevSign.current && sign !== 0) {
      setFlash(sign > 0 ? 'pos' : 'neg');
      const id = setTimeout(() => setFlash(null), 240);
      prevSign.current = sign;
      return () => clearTimeout(id);
    }
    prevSign.current = sign;
  }, [t.spotChangePct]);

  const positive = (t.spotChange ?? 0) >= 0;
  const regimeColor = t.regime === 'POSITIVE_GAMMA' ? 'cyan' : t.regime === 'NEGATIVE_GAMMA' ? 'red' : '';
  const regimeDotClass = t.regime === 'POSITIVE_GAMMA' ? 'live' : t.regime === 'NEGATIVE_GAMMA' ? 'dead' : 'warn';

  return (
    <div className={`gv-ambient${flash ? ` flash-${flash}` : ''}`} role="status" aria-label={`${ticker} ambient telemetry`}>
      <span className="gv-tick">
        <span className={`dot ${regimeDotClass}`} aria-hidden="true" />
        <span className="label">{ticker} REGIME</span>
        <span className={`val ${regimeColor}`}>{t.regime.replace('_', ' ')}</span>
      </span>

      <span className="gv-tick">
        <span className="label">{ticker}</span>
        <span className="val">{fmtMoney(tweenedSpot)}</span>
        {t.spotChange != null && (
          <span className={`val ${positive ? 'green' : 'red'}`}>
            {fmtSigned(t.spotChange)} ({fmtSignedPct(tweenedPct)})
          </span>
        )}
      </span>

      <span className="gv-tick optional">
        <span className="label">GEX FLIP</span>
        <span className="val cyan">{t.flip != null ? `$${formatStrike(t.flip)}` : '—'}</span>
      </span>

      <span className="gv-tick optional">
        <span className="label">KING</span>
        <span className="val">{t.king ? `$${formatStrike(t.king.strike)}` : '—'}</span>
        {t.king?.distancePct != null && (
          <span className="val">({fmtSignedPct(t.king.distancePct)})</span>
        )}
      </span>

      <AgeChip status={t.connection} lastUpdate={t.lastUpdate} />

      <ClockChip />
    </div>
  );
};

export default AmbientTicker;
